import { Box, Divider, Stack, TextField, Typography } from '@mui/material';
import { Provider } from '@repo/api-gw-sdk';
import { useState } from 'react';

import { BulletItem } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/bulletItem';
import { extractErrorMessage } from '@/app/(blank-layout-pages)/(authenticated)/onboarding/errorHandling';
import { TerraformBashToggle } from '@/components/accounts/terraformBashToggle';
import { StepErrorDialog } from '@/components/dialogs/stepErrorDialog';
import { CustomAccountDisplayName } from '@/components/form/customAccountDisplayName';
import { CopyableCode } from '@/components/shared/copyableCode';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';

import { getMongoAtlasSourceOrganizationCode } from './mongoAtlasCode';

export interface MongoAtlasConnectOrganizationState {
  connectMethod: 'terraform' | 'bash';
  apiPublicKey: string;
  apiPrivateKey: string;
}

export const ConnectMongoAtlasSourceOrganization = (
  props: StepProps<MongoAtlasConnectOrganizationState>
) => {
  const [isDeploying, setIsDeploying] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [customName, setCustomName] = useState('');
  const user = useUser();
  const dal = useDAL();

  const { data: scanningAccounts } = dal.cloudAccounts.scanning.useList();
  const scanningAccountId =
    scanningAccounts?.accounts.find(
      (account) => account.cloudProvider === Provider.Aws
    )?.providerAccountId || '';

  const code = getMongoAtlasSourceOrganizationCode(
    user.eonAccount?.id ?? '',
    scanningAccountId,
    props.wizardState.connectMethod
  );

  const isMissingCodeFields = !scanningAccountId;

  const canGoNext = () => {
    const hasApiKeys =
      !!props.wizardState.apiPublicKey && !!props.wizardState.apiPrivateKey;

    return !isDeploying && !!scanningAccountId && hasApiKeys;
  };

  const onNextClick = async () => {
    setError(null);
    setIsDeploying(true);

    try {
      await dal.cloudAccounts.atlasOrganization.connect({
        name: customName || undefined,
        apiPublicKey: props.wizardState.apiPublicKey,
        apiPrivateKey: props.wizardState.apiPrivateKey,
      });
      await props.finish();
    } catch (error: unknown) {
      const errorMessage = await extractErrorMessage(error);
      setError(new Error(errorMessage));
    } finally {
      setIsDeploying(false);
    }
  };

  const bulletTitle = `${
    props.wizardState.connectMethod === 'terraform'
      ? 'Apply the generated Terraform module'
      : 'Run the generated Bash command'
  } in your environment, then enter the Eon API key from the output below.`;

  return (
    <>
      <StepContainer
        stepperIndex={0}
        hideStepper
        stepperLabels={props.stepperLabels}
        canGoNext={canGoNext}
        nextButtonDisabledTooltip={
          isDeploying
            ? 'Please wait for the deployment to complete'
            : 'Please fill all required fields'
        }
        nextButtonText='Connect'
        backButtonText='Cancel'
        onBackClick={props.back}
        sx={{ overflow: 'auto', paddingX: '0px', paddingTop: '24px' }}
        onNextClick={onNextClick}
      >
        <Stack divider={<Divider />} gap='24px'>
          <TerraformBashToggle
            text='Run the setup below using your own Atlas API key with Organization Owner permissions. It will create a dedicated Eon API key with minimal permissions.'
            connectMethod={props.wizardState.connectMethod}
            setConnectMethod={(connectMethod) => {
              props.setWizardState({ ...props.wizardState, connectMethod });
            }}
          />
          <Stack gap='24px' divider={<Divider />}>
            <Box padding='16px min(100px,6vw) 36px' width='100%' height='auto'>
              <BulletItem
                title={bulletTitle}
                leftContent={
                  <Stack
                    gap='24px'
                    divider={<Divider sx={{ borderStyle: 'dashed' }} />}
                  >
                    <Stack gap='24px'>
                      <Typography variant='body2' color='text.secondary'>
                        After running the setup, enter the Eon API key from the
                        output below.
                      </Typography>
                      <TextField
                        label='Eon API Public Key'
                        value={props.wizardState.apiPublicKey}
                        onChange={(e) =>
                          props.setWizardState({
                            ...props.wizardState,
                            apiPublicKey: e.target.value,
                          })
                        }
                        disabled={isDeploying}
                        fullWidth
                        size='small'
                      />
                      <TextField
                        label='Eon API Private Key'
                        type='password'
                        value={props.wizardState.apiPrivateKey}
                        onChange={(e) =>
                          props.setWizardState({
                            ...props.wizardState,
                            apiPrivateKey: e.target.value,
                          })
                        }
                        disabled={isDeploying}
                        fullWidth
                        size='small'
                      />
                    </Stack>
                    <CustomAccountDisplayName
                      entityName='organization'
                      customName={customName}
                      setCustomName={setCustomName}
                    />
                  </Stack>
                }
                rightContent={
                  <CopyableCode
                    title={
                      props.wizardState.connectMethod === 'terraform'
                        ? 'main.tf'
                        : 'mongo-atlas-eon-setup.sh'
                    }
                    code={code}
                    blur={isMissingCodeFields}
                  />
                }
              />
            </Box>
          </Stack>
        </Stack>
      </StepContainer>
      <StepErrorDialog
        isOpen={!!error}
        onClose={() => {
          setError(null);
          setIsDeploying(false);
        }}
        error={error}
      />
    </>
  );
};
